import { useLazyGetAuctionForms } from "apollo/hooks/queries";
import { isRehabFormType, RECARE_FILE_TYPE } from "core/consts";
import { useIntParams } from "core/model/utils/urls";
import { FileForm, RehabFormName } from "core/types";
import { useCallback } from "react";
import { convertEncryptedFieldIn } from "react-forms-state";
import { useTranslations } from "translations";
import { useCreateRehaPdf } from "../PatientForms/RehabForms/useCreateRehaPdf";
import { useCreateTransitionalCarePdf } from "../PatientForms/useCreateTransitionalCarePdf";
import { getFormTitleFromFormType } from "./utils";

export function useFetchFormDataAndGeneratePdf({
  externalId,
}: {
  externalId: string;
}) {
  const { auctionId } = useIntParams<{
    auctionId: number;
  }>();

  const getAuctionForms = useLazyGetAuctionForms();

  const createRehaPdf = useCreateRehaPdf();
  const createTransitionalCarePdf = useCreateTransitionalCarePdf();
  const translations = useTranslations();

  const fetchAndGeneratePdf = useCallback(
    async ({ file }: { file: FileForm }) => {
      const auctionForms = await getAuctionForms({ auctionId });

      if (file.recare_file_type === RECARE_FILE_TYPE.TRANSITIONAL_CARE) {
        const form = auctionForms?.find(
          (f) => f.form_type === RECARE_FILE_TYPE.TRANSITIONAL_CARE,
        );
        if (!form) {
          throw new Error("Failed to fetch transitional care");
        }
        const pdfBytes = await createTransitionalCarePdf({
          transitionalCare: form,
        });
        return {
          fileName: `${translations.patientForms.transitionalCareForm.title}.pdf`,
          pdfBytes,
        };
      }

      const rehabForms = auctionForms?.filter((f) =>
        isRehabFormType(f.form_type),
      );

      if (!rehabForms?.length) {
        throw new Error("Failed to fetch rehab forms");
      }

      const form = rehabForms.find(
        (f) => f.form_type === file.recare_file_type,
      );
      if (!form?.form_data) {
        throw new Error("Form not found or no form data for PDF generation");
      }

      const formTitle = getFormTitleFromFormType(form.form_type!, translations);
      const stringified = convertEncryptedFieldIn(form.form_data as Record<string, any>);
      const formData = JSON.parse(stringified ?? "{}");

      const formName: RehabFormName = (() => {
        switch (form.form_type) {
          case RECARE_FILE_TYPE.GKV_GENERAL_FORM:
            return "gkv-form-general";
          case RECARE_FILE_TYPE.GKV_MEDICAL_FORM:
            return "gkv-form-medical";
          case RECARE_FILE_TYPE.DRV_GENERAL_FORM:
            return "drv-form-general";
          case RECARE_FILE_TYPE.DRV_MEDICAL_FORM:
            return "drv-form-medical";
          default:
            throw new Error(`Unknown form_type: ${form.form_type}`);
        }
      })();

      const pdfBytes = await createRehaPdf({
        auctionForms: rehabForms,
        documentNumber: -1,
        externalId,
        formData,
        formName,
      });

      return { fileName: `${formTitle}.pdf`, pdfBytes };
    },
    [
      auctionId,
      externalId,
      getAuctionForms,
      createRehaPdf,
      createTransitionalCarePdf,
    ],
  );

  return fetchAndGeneratePdf;
}
